import React, { useEffect, useReducer } from 'react';
import { EMPTY_FORM_TOKEN_CHAIN, ENGLISH } from '../../../constants';
import { getIsMobile } from '../../../utils/helpers';
import useLocalStorageKey from '../../../hooks/useLocalStorageKey';
import { persistUserState } from '../../../utils/persistUserState';
import { persistFormTokens } from '../../../utils/persistFormTokens';
import { useCfgParam } from '../../../hooks/useCfgParam';
import { FormTokenChainState, UserState } from '../../../types/context';
import { UserStateContext } from '../UserContext';
import { userStateReducer } from '../utils/userStateReducer';

interface Props {
  children: React.ReactNode;
  mockState?: Partial<UserState>;
  initialFormTokens?: FormTokenChainState;
}

const isMobile = getIsMobile();

export const HydratedUserStateProvider = ({
  children,
  mockState,
  initialFormTokens,
}: Props) => {
  const localStorageKey = useLocalStorageKey();

  const storedItem = window.localStorage.getItem(localStorageKey) || '{}';
  const persistedState: Partial<UserState> = JSON.parse(storedItem);
  const {
    venueName: _persistedVenueName,
    venueNameStatus: _persistedVenueNameStatus,
    ...safePersistedState
  } = persistedState;

  const defaultState: UserState = {
    token: null,
    language: useCfgParam('defaultLanguage') || ENGLISH,
    usePassword: false,
    useOtp: false,
    username: null,
    userLogin: null,
    apiEndpoint: '',
    endpointCorrect: false,
    branchName: isMobile ? '' : useCfgParam('branchName'),
    venueName: null,
    venueNameStatus: 'loading',
    lastActivityTime: 0,
    loggedInAt: 0,
    keepLoggedIn: false,
    dashboards: [],
    reports: [],
    filterValues: {},
    globalFilterValues: {},
    endpointPageStep: 1,
    endpointPageManualEntering: false,
    endpointAlreadyEntered: false,
    sessionErrorPending: false,
    autologoutPending: false,
    menuItems: [],
    menuId: null,
    defaultCurrencyFormat: null,
    defaultNumberFormat: null,
    formTokens: EMPTY_FORM_TOKEN_CHAIN,
  };

  const [state, dispatch] = useReducer(userStateReducer, {
    ...defaultState,
    ...safePersistedState,
    ...(initialFormTokens && { formTokens: initialFormTokens }),
    ...(mockState && { ...mockState }),
  });

  useEffect(() => {
    persistUserState(localStorageKey, state);
  }, [state]);

  useEffect(() => {
    persistFormTokens(localStorageKey, state.formTokens);
  }, [state.formTokens]);

  const value = { state, dispatch };

  return (
    <UserStateContext.Provider value={value}>
      {children}
    </UserStateContext.Provider>
  );
};
